// Videocall Consulenze — website request service (newarc-site #438, Stream A).
//
// Pure builders + thin orchestration for the "book a 30-min videocall" flow.
// Firebase access is injected (db / sendEmail) so the logic is testable without
// mounting Vue or hitting the real SDK. See docs/videocall/WEBSITE_SPEC.md.

import VideocallRequest from "../classes/VideocallRequest";
import { isPrivacyConsent } from "../consent/privacyConsent.js";
import { isShareConsent } from "../consent/shareConsent.js";

export const NEWARC_LOGO =
  "https://0t36l.mjt.lu/img2/0t36l/52e01620-54c0-46b4-9ec2-bf830ad89884/content";
export const IMMAGINACASA_LOGO =
  "https://0t36l.mjt.lu/img2/0t36l/66f24792-4d70-4c72-88f5-f997b3baaeb5/content";

// Sender + logo per brand — mirrors the platform's sendMadreEmail() mapping.
const BRANDS = {
  newarc: { senderEmail: "noreply@newarc.it", senderName: "Newarc", logoUrl: NEWARC_LOGO },
  immaginacasa: {
    senderEmail: "noreply@immaginacasa.com",
    senderName: "Immaginacasa",
    logoUrl: IMMAGINACASA_LOGO,
  },
};

// Resolve a brand to its sender/logo config (defaults to newarc, like the platform).
export function brandConfig(brand = "newarc") {
  return BRANDS[brand] || BRANDS.newarc;
}

// Shared all-purpose Mailjet "madre" template (confirmed across the 3 repos).
export const VIDEOCALL_EMAIL_TEMPLATE_ID = 8097000;

// Preferred days of the week (multi-select, >= 1 required).
export const VIDEOCALL_DAYS = [
  { value: "lunedi", label: "Lunedì" },
  { value: "martedi", label: "Martedì" },
  { value: "mercoledi", label: "Mercoledì" },
  { value: "giovedi", label: "Giovedì" },
  { value: "venerdi", label: "Venerdì" },
];

// Preferred time slots (multi-select, >= 1 required).
export const VIDEOCALL_TIME_SLOTS = [
  { value: "09-11", label: "09.00 - 11.00" },
  { value: "11-13", label: "11.00 - 13.00" },
  { value: "14-16", label: "14.00 - 16.00" },
  { value: "16-18", label: "16.00 - 18.00" },
];

// Objectives (multi-select, may be empty). Defined once, composed into the two
// UI sets and a single validity list so a value is validated regardless of which
// form offered it.
const OBJ_QUESTO_IMMOBILE = { value: "questo-immobile", label: "La ristrutturazione di questo immobile" };
const OBJ_GENERALE = { value: "generale", label: "Ristrutturare un immobile in generale" };
const OBJ_DA_ANNUNCIO = { value: "da-annuncio", label: "Ristrutturare l'immobile da un annuncio" };

// UI set used inside a listing (unchanged).
export const VIDEOCALL_OBJECTIVES = [OBJ_QUESTO_IMMOBILE, OBJ_GENERALE];
// UI set used on the standalone /prenota-videocall page.
export const VIDEOCALL_OBJECTIVES_STANDALONE = [OBJ_GENERALE, OBJ_DA_ANNUNCIO];
// Every objective value accepted on write.
const ALL_OBJECTIVES = [OBJ_QUESTO_IMMOBILE, OBJ_GENERALE, OBJ_DA_ANNUNCIO];

// Fixed internal recipient — never client-controlled (anti open-relay).
export const VIDEOCALL_INTERNAL_RECIPIENTS = [
  { email: "amministrazione@newarc.it", name: "Amministrazione" },
];

const HTML_ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

// HTML-escape any value interpolated into an email body/subject.
export function escapeHtml(value) {
  return (value ?? "").replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
}

const isOneOf = (options, value) => options.some((o) => o.value === value);
export const isValidDay = (v) => isOneOf(VIDEOCALL_DAYS, v);
export const isValidTimeSlot = (v) => isOneOf(VIDEOCALL_TIME_SLOTS, v);
export const isValidObjective = (v) => ALL_OBJECTIVES.some((o) => o.value === v);

// Objective key that unlocks the optional "property link" field in a listing form.
export const VIDEOCALL_GENERAL_OBJECTIVE = "generale";
// Objectives that carry an optional property link (listing: "generale",
// standalone: "da-annuncio"). Used to decide whether the link is stored.
export const VIDEOCALL_LINK_OBJECTIVES = ["generale", "da-annuncio"];

// Max length stored for the free-text notes (defensive; the textarea also caps it).
export const VIDEOCALL_NOTES_MAX_LENGTH = 1000;

// --- Optional property link (only meaningful with the "generale" objective) ---

// Trim and, when a value is present without a scheme, prepend https://.
// Empty/blank input yields "".
export function normalizeLink(value) {
  const v = String(value ?? "").trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

// Light validation: a non-empty value that looks like a "domain.tld[/...]" URL.
// Empty input is NOT valid here — callers treat the empty link as "omitted".
export function isValidLink(value) {
  return /^https?:\/\/[^\s.]+\.[^\s]{2,}/i.test(normalizeLink(value));
}

// Build the Mailjet template (8097000) Variables. The template's field names are
// inconsistent across the codebase — the Flutter platform uses lowercase
// (mailtitle/mailtext/logourl) while newarc-cloud-functions' sendMadreEmail uses
// camelCase (mailTitle/mailBody/logoUrl). We send BOTH casings so the title,
// body and (previously-missing) logo render regardless of the template's exact
// field names. Unused Mailjet variables are ignored.
export function madreVariables({ title, body, logoUrl }) {
  
  return {
    mailtitle: title,
    mailTitle: title,
    mailtext: body,
    mailBody: body,
    logourl: logoUrl
  };
}

// --- Italian phone formatting (+39, grouped local number) ---

// Extract the local digits (max 10), dropping an Italian country code if present.
export function localPhoneDigits(value) {
  let d = String(value ?? "").replace(/\D/g, "");
  if (d.startsWith("0039")) d = d.slice(4);
  else if (d.startsWith("39") && d.length > 10) d = d.slice(2);
  return d.slice(0, 10);
}

// Group the local number as 3-3-4 (spaces), progressively while typing.
export function formatPhoneLocal(value) {
  const d = localPhoneDigits(value);
  return [d.slice(0, 3), d.slice(3, 6), d.slice(6, 10)].filter(Boolean).join(" ");
}

// Full number with the +39 prefix; empty string when there are no digits.
export function fullItalianPhone(value) {
  const local = formatPhoneLocal(value);
  return local ? `+39 ${local}` : "";
}

// Build a human-readable address from an ad's addressInfo object,
// mirroring the title logic in SingleProperty.vue (streetName streetNumber, city).
export function buildAdAddress(addressInfo) {
  const a = addressInfo || {};
  let street = "";
  if (a.streetName) street += a.streetName;
  if (a.streetNumber) street += (street ? " " : "") + a.streetNumber;
  let s = street;
  if (a.city) s += (s ? ", " : "") + a.city;
  return s.trim();
}

// Build exactly the website-owned `videocallRequests` contract fields.
// Throws on invalid input before any write is attempted.
export function buildVideocallRequestDoc({
  uid,
  clientName,
  clientEmail,
  clientPhone,
  newarcHomeId,
  adCode,
  adAddress,
  preferredDays,
  preferredTimeSlots,
  objectives,
  notes,
  generalPropertyLink,
  source = "immaginacasa-ad",
  privacyConsent = null,
  shareConsent = null,
  now,
}) {
  const days = (preferredDays || []).filter(isValidDay);
  const slots = (preferredTimeSlots || []).filter(isValidTimeSlot);
  const objs = (objectives || []).filter(isValidObjective);

  if (!uid) throw new Error("AUTH_REQUIRED");
  if (days.length === 0) throw new Error("DAYS_REQUIRED");
  if (slots.length === 0) throw new Error("TIMESLOTS_REQUIRED");
  if (!clientPhone || !clientPhone.trim()) throw new Error("PHONE_REQUIRED");
  // Nessuna richiesta senza consenso privacy registrato (#723). La regola
  // Firestore ammette in privacyConsent solo le cinque chiavi di
  // buildPrivacyConsent: si salva l'oggetto così com'è.
  if (!isPrivacyConsent(privacyConsent)) throw new Error("PRIVACY_REQUIRED");
  // #3234: consenso al partner di ristrutturazione (pagine Immaginacasa). Se
  // c'è deve essere esatto: la regola Firestore validShareConsent lo verifica.
  if (shareConsent !== null && !isShareConsent(shareConsent)) throw new Error("SHARE_CONSENT_INVALID");

  // The link is only kept when one of the link-bearing objectives is selected.
  const link = objs.some((o) => VIDEOCALL_LINK_OBJECTIVES.includes(o))
    ? normalizeLink(generalPropertyLink)
    : "";

  const ts = now ?? Date.now();
  return {
    source,
    clientUserId: uid, // AUTH uid (== newarcUser.id), NOT firebaseId
    clientName: (clientName || "").trim(),
    clientEmail: (clientEmail || "").trim().toLowerCase(),
    clientPhone: clientPhone.trim(),
    newarcHomeId: newarcHomeId || "",
    adCode: adCode || "",
    adAddress: adAddress || "",
    preferredDays: days,
    preferredTimeSlots: slots,
    objectives: objs,
    notes: String(notes ?? "").trim().slice(0, VIDEOCALL_NOTES_MAX_LENGTH),
    generalPropertyLink: link,
    requestStatus: "da-prenotare",
    createdAt: ts,
    updatedAt: ts,
    isArchived: false,
    privacyConsent,
    ...(shareConsent ? { shareConsent } : {}),
  };
}

export function buildClientEmailPayload({ adAddress, clientEmail, clientName, brand = "immaginacasa" }) {
  const b = brandConfig(brand);
  return {
    templateId: VIDEOCALL_EMAIL_TEMPLATE_ID,
    sendingEmail: b.senderEmail,
    sendingName: b.senderName,
    subject: "La tua videochiamata",
    variables: madreVariables({
      title: "La tua videochiamata",
      body: `Abbiamo ricevuto la tua richiesta per una videochiamata di 30 minuti, per discutere dell'immobile di ${escapeHtml(
        adAddress
      )}. Un progettista ti contatterà il prima possibile per prendere un appuntamento.`,
      logoUrl: b.logoUrl,
    }),
    recipients: [{ email: clientEmail, name: clientName }],
  };
}

export function buildInternalEmailPayload({ adAddress, adCode, clientName, brand = "immaginacasa" }) {
  const b = brandConfig(brand);
  return {
    templateId: VIDEOCALL_EMAIL_TEMPLATE_ID,
    sendingEmail: b.senderEmail,
    sendingName: b.senderName,
    subject: "Richiesta videochiamata",
    variables: madreVariables({
      title: "Richiesta videochiamata",
      body: `Richiesta videochiamata per l'immobile di ${escapeHtml(adAddress)} - ${escapeHtml(
        adCode
      )} da parte di ${escapeHtml(clientName)}.`,
      logoUrl: b.logoUrl,
    }),
    recipients: VIDEOCALL_INTERNAL_RECIPIENTS,
  };
}

// --- General (standalone page) request emails ---
// Used by /prenota-videocall, where there is no specific property. The copy does
// not reference an immobile; the internal mail includes the pasted annuncio link
// when the user provided one.

export function buildGeneralClientEmailPayload({ clientEmail, clientName, brand = "immaginacasa" }) {
  const b = brandConfig(brand);
  return {
    templateId: VIDEOCALL_EMAIL_TEMPLATE_ID,
    sendingEmail: b.senderEmail,
    sendingName: b.senderName,
    subject: "La tua videochiamata",
    variables: madreVariables({
      title: "La tua videochiamata",
      body: "Abbiamo ricevuto la tua richiesta per una videochiamata di 30 minuti. Un progettista ti contatterà il prima possibile per prendere un appuntamento.",
      logoUrl: b.logoUrl,
    }),
    recipients: [{ email: clientEmail, name: clientName }],
  };
}

export function buildGeneralInternalEmailPayload({ clientName, generalPropertyLink, brand = "immaginacasa" }) {
  const b = brandConfig(brand);
  const link = normalizeLink(generalPropertyLink);
  const linkLine = link ? `<br>Annuncio: ${escapeHtml(link)}` : "";
  return {
    templateId: VIDEOCALL_EMAIL_TEMPLATE_ID,
    sendingEmail: b.senderEmail,
    sendingName: b.senderName,
    subject: "Richiesta videochiamata",
    variables: madreVariables({
      title: "Richiesta videochiamata",
      body: `Nuova richiesta di videochiamata (consulenza generale) da parte di ${escapeHtml(
        clientName
      )}.${linkLine}`,
      logoUrl: b.logoUrl,
    }),
    recipients: VIDEOCALL_INTERNAL_RECIPIENTS,
  };
}

export async function sendGeneralVideocallRequestEmails({ sendEmail, clientEmail, clientName, generalPropertyLink }) {
  await sendEmail(buildGeneralClientEmailPayload({ clientEmail, clientName }));
  await sendEmail(buildGeneralInternalEmailPayload({ clientName, generalPropertyLink }));
}

// ---------------------------------------------------------------------------
// Option 1 — "Contact us" emails.
// NOTE: copy below is PROVISIONAL. The exact contact-form fields and email
// wording are owned by Stefano (see docs/videocall/WEBSITE_SPEC.md §12) and
// must be confirmed before go-live. The mechanism (template, escaping,
// recipients) matches Option 2.
// ---------------------------------------------------------------------------

export function buildContactClientEmailPayload({ adAddress, clientEmail, clientName, brand = "immaginacasa" }) {
  const b = brandConfig(brand);
  return {
    templateId: VIDEOCALL_EMAIL_TEMPLATE_ID,
    sendingEmail: b.senderEmail,
    sendingName: b.senderName,
    subject: "La tua richiesta",
    variables: madreVariables({
      title: "La tua richiesta",
      body: `Abbiamo ricevuto la tua richiesta di contatto per l'immobile di ${escapeHtml(
        adAddress
      )}. Ti risponderemo il prima possibile.`,
      logoUrl: b.logoUrl,
    }),
    recipients: [{ email: clientEmail, name: clientName }],
  };
}

export function buildContactInternalEmailPayload({
  adAddress,
  adCode,
  clientName,
  clientEmail,
  clientPhone,
  message,
  brand = "immaginacasa",
}) {
  const b = brandConfig(brand);
  return {
    templateId: VIDEOCALL_EMAIL_TEMPLATE_ID,
    sendingEmail: b.senderEmail,
    sendingName: b.senderName,
    subject: "Nuova richiesta di contatto",
    variables: madreVariables({
      title: "Nuova richiesta di contatto",
      body: `Richiesta di contatto per l'immobile di ${escapeHtml(adAddress)} - ${escapeHtml(
        adCode
      )} da parte di ${escapeHtml(clientName)} (${escapeHtml(clientEmail)}, ${escapeHtml(
        clientPhone
      )}).<br>Messaggio: ${escapeHtml(message)}`,
      logoUrl: b.logoUrl,
    }),
    recipients: VIDEOCALL_INTERNAL_RECIPIENTS,
  };
}

export async function sendContactEmails({
  sendEmail,
  adAddress,
  adCode,
  clientEmail,
  clientName,
  clientPhone,
  message,
}) {
  await sendEmail(buildContactClientEmailPayload({ adAddress, clientEmail, clientName }));
  await sendEmail(
    buildContactInternalEmailPayload({ adAddress, adCode, clientName, clientEmail, clientPhone, message })
  );
}

// If the profile has no phone yet and the user entered one in the form, persist
// it to the profile. Injected `updateUserInfo` keeps this testable. Returns
// whether a save was attempted/succeeded.
export async function maybeSaveProfilePhone({ updateUserInfo, profilePhone, enteredPhone }) {
  const entered = (enteredPhone || "").trim();
  if (!entered) return false;
  if (profilePhone && profilePhone.trim()) return false; // profile already has a phone
  return await updateUserInfo({ phone: entered });
}

// Write one validated request doc; returns the new doc id. Throws on
// validation failure (before writing) or on a write failure.
export async function createVideocallRequest({ db, ...fields }) {
  const doc = buildVideocallRequestDoc(fields);
  const ref = await db.collection("videocallRequests").add(doc);
  return ref.id;
}

// List the signed-in user's own videocall requests, newest first. `db` injected
// for testability. Returns VideocallRequest instances. Read is rules-gated to
// own requests (clientUserId == auth.uid).
export async function fetchUserVideocallRequests({ db, clientUserId }) {
  const snap = await db
    .collection("videocallRequests")
    .where("clientUserId", "==", clientUserId)
    .orderBy("createdAt", "desc")
    .get();
  return snap.docs.map((d) => new VideocallRequest(d.id, d.data()));
}

// Fetch a single request by id, or null if it does not exist. The read rule
// still enforces ownership, so a non-owned id resolves to a permission error.
export async function fetchVideocallRequestById({ db, docId }) {
  const ref = await db.collection("videocallRequests").doc(docId).get();
  if (!ref.exists) return null;
  return new VideocallRequest(ref.id, ref.data());
}

// Send the two "request received" emails (client first, then internal).
// Caller decides how to treat failures (spec: soft — the request still stands).
export async function sendVideocallRequestEmails({ sendEmail, adAddress, adCode, clientEmail, clientName }) {
  await sendEmail(buildClientEmailPayload({ adAddress, clientEmail, clientName }));
  await sendEmail(buildInternalEmailPayload({ adAddress, adCode, clientName }));
}
